import { useEffect, useRef, useState } from "react";

const SDK_URL = "https://static.matterport.com/showcase-sdk/latest.js";

function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (window.MP_SDK) return resolve();
    let s = document.querySelector(`script[src="${src}"]`);
    if (!s) {
      s = document.createElement("script");
      s.src = src;
      document.head.appendChild(s);
    }
    s.addEventListener("load", () => resolve());
    s.addEventListener("error", reject);
  });
}

export function showcaseUrl(modelId, key, edit = false) {
  if (!modelId || !key) return null;
  const hideModes = edit ? "" : "&dh=0&fp=0";
  return `https://my.matterport.com/show?m=${modelId}&applicationKey=${key}&play=1&qs=1&brand=0&title=0&help=0&hl=0&mls=2${hideModes}&f=0&measurements=0&gt=0&hr=0&tagNav=0&search=0&vr=0&mt=0&pin=0&portal=0&nt=0`;
}

export function useMatterport(modelId, sdkKey, edit = false) {
  const iframeRef = useRef(null);
  const poseRef = useRef(null);
  const hitRef = useRef(null);
  const sweepsRef = useRef({});
  const [sdk, setSdk] = useState(null);
  const [status, setStatus] = useState("loading");
  const src = showcaseUrl(modelId, sdkKey, edit);

  useEffect(() => {
    if (!src) {
      setStatus("unavailable");
      return undefined;
    }
    let cancelled = false;
    const subs = [];
    setStatus("loading");
    const timer = setTimeout(() => { if (!cancelled) setStatus((s) => (s === "ready" ? s : "blocked")); }, 25000);
    loadScript(SDK_URL)
      .then(() => window.MP_SDK.connect(iframeRef.current, sdkKey, ""))
      .then((mp) => {
        if (cancelled) return;
        clearTimeout(timer);
        setSdk(mp);
        setStatus("ready");
        subs.push(mp.Camera.pose.subscribe((p) => { poseRef.current = p; }));
        subs.push(mp.Sweep.data.subscribe({ onCollectionUpdated: (c) => { sweepsRef.current = c; } }));
        subs.push(mp.Pointer.intersection.subscribe((h) => {
          if (h?.position) hitRef.current = { x: h.position.x, y: h.position.y, z: h.position.z };
        }));
      })
      .catch((e) => {
        console.warn("Matterport SDK connect failed", e);
        if (!cancelled) setStatus("blocked");
      });
    return () => {
      cancelled = true;
      clearTimeout(timer);
      subs.forEach((s) => s?.cancel?.());
    };
  }, [src, sdkKey]);

  return { iframeRef, poseRef, hitRef, sweepsRef, sdk, status, src };
}

export function nearestSweep(sweeps, pos) {
  let best = null, bd = Infinity;
  Object.values(sweeps || {}).forEach((s) => {
    const p = s.position;
    if (!p || s.enabled === false) return;
    const d = (p.x - pos.x) ** 2 + (p.y - pos.y) ** 2 + (p.z - pos.z) ** 2;
    if (d < bd) { bd = d; best = s.sid || s.id; }
  });
  return best;
}

export function snapshotPose(p) {
  if (!p) return null;
  return {
    sweep: p.sweep,
    mode: p.mode,
    rotation: { x: p.rotation.x, y: p.rotation.y },
    position: { x: p.position.x, y: p.position.y, z: p.position.z },
  };
}

const TRANSITION = { fly: ["FLY", 1800], fade: ["FADEOUT", 1000], instant: ["INSTANT", 0] };

const SPEED = { slow: 1.7, normal: 1, fast: 0.55 };

export async function jumpTo(sdk, pose, mode = "fly", speed = "normal", sweeps = null) {
  if (!sdk || !pose) return false;
  const list = sweeps && Object.values(sweeps);
  const known = (sid) => !list?.length || list.some((s) => (s.sid || s.id) === sid && s.enabled !== false);
  let sweep = pose.sweep && known(pose.sweep) ? pose.sweep : null;
  if (!sweep && pose.position) sweep = nearestSweep(sweeps, pose.position);
  if (!sweep) return false;
  const [t, base] = TRANSITION[mode] || TRANSITION.fly;
  const ms = Math.round(base * (SPEED[speed] || 1));
  const rot = pose.rotation && Number.isFinite(pose.rotation.x) ? { x: pose.rotation.x, y: pose.rotation.y } : undefined;
  const attempts = [
    () => sdk.Sweep.moveTo(sweep, { rotation: rot, transition: sdk.Sweep.Transition[t], transitionTime: ms }),
    () => sdk.Sweep.moveTo(sweep, { transition: sdk.Sweep.Transition.FLY }),
    async () => { await sdk.Mode.moveTo(sdk.Mode.Mode.INSIDE); await sdk.Sweep.moveTo(sweep, { transition: sdk.Sweep.Transition.INSTANT }); },
  ];
  for (const a of attempts) {
    try { await a(); return true; } catch (e) { console.warn("moveTo failed", e); }
  }
  if (rot) { try { await sdk.Camera.setRotation(rot); return true; } catch { /* already here */ } }
  return false;
}
